/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { of, Subject, Observable } from 'rxjs';
import { Hotel } from '@app/hotels/hotels/hotel.model';
import { ApiQuery, GeoFenceFilter } from '@app/core/http/api-query';
import { FormGroup } from '@angular/forms';
import { HotelsService } from '@app/hotels/hotels/hotels.service';
import { HotelsFormService } from '@app/hotels/hotels/hotels-form.service';
import { distinctUntilChanged, debounceTime, switchMap, tap, catchError } from 'rxjs/operators';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { Logger } from '@app/core';

const log = new Logger('HotelSelectComponent');

@Component({
  selector: 'app-hotel-select',
  templateUrl: './hotel-select.component.html',
  styleUrls: ['./hotel-select.component.scss'],
})
export class HotelSelectComponent implements OnInit {
  data: Observable<Hotel[]> = of([]);
  apiQuery: ApiQuery = new ApiQuery();
  loader: boolean;
  @Input() parentFormGroup: FormGroup;
  input$ = new Subject<string>();
  @Input() model: any = 'undefined';
  @Output() modelChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() hideLabel = false;
  @Input() onlyActive = true;
  @Output() select = new EventEmitter();
  @Input() passFormControlName = 'hotel_id';
  hotelFormControlName: any = this.passFormControlName.replace('_id', '');
  @Input() geofenceFilter: any = null;
  @Input() filter: string = null;
  @Input() filterValue: any = null;
  lastResponse: any = null;
  @Input() includes: string = null;
  @Input() isInModal = true;

  constructor(private hotelService: HotelsService, private hotelFormService: HotelsFormService) {}

  ngOnInit() {
    if (this.model === 'undefined') {
      this.hotelFormControlName = this.passFormControlName.replace('_id', '');

      const preSelectedHotel = this.parentFormGroup.get(this.hotelFormControlName);
      const preSelectedHotelId = this.parentFormGroup.get(this.passFormControlName);

      /**
       * Попълва избрания хотел на едит.
       * Ако има FormControl 'hotel' в parentFormGroup.
       * Ако няма ще се опита да го get-не по ID
       */
      if (preSelectedHotel) {
        // If there's already a value, set it immediately so the select shows it
        const current = preSelectedHotel.value;
        if (current && current.name) {
          this.data = of([current]);
        }

        /*
         * Следи за промени в hotel FormControl-a и когато намери валиден Hotel спира да следи
         */
        const preselectedHotelSubscriber = preSelectedHotel.valueChanges.subscribe((hotel: any) => {
          if (hotel && hotel.name) {
            this.data = of([hotel]);
            preselectedHotelSubscriber.unsubscribe();
          }
        });
      } else if (preSelectedHotelId) {
        // If there's already an id value, load hotel immediately
        const currentId = preSelectedHotelId.value;
        if (currentId) {
          this.loadHotelById(currentId);
        }

        /*
         * Ако не е намерен hotel FormControl-a търси hotel_id FormControl и се опитва да get-не хотела по ID
         */
        const preselectedHotelIdSubscriber = preSelectedHotelId.valueChanges.subscribe((hotelId: any) => {
          if (hotelId) {
            this.loadHotelById(hotelId);
            preselectedHotelIdSubscriber.unsubscribe();
          }
        });
      }
    } else if (this.model !== null && this.model !== '') {
      this.data = of([this.model]);
    }
    this.hotelSearch();
  }

  // loadHotels(keyword?: string) {
  //   this.loader = true;
  //
  //   this.hotelService.index(this.apiQuery.addFilter('name', keyword)).subscribe((response: PaginatedResponse) => {
  //     this.data = of(response.data);
  //     this.loader = false;
  //   });
  // }

  loadHotelById(id: number) {
    this.loader = true;

    const apiQuery = new ApiQuery();
    if (this.includes) {
      apiQuery.addIncludes(this.includes);
    }

    this.hotelService.show(id, apiQuery).subscribe((hotel: Hotel) => {
      this.data = of([hotel]);
      this.loader = false;
    });
  }

  hotelSearch() {
    if (this.onlyActive) {
      this.apiQuery.addFilter('active', true);
    }

    if (this.includes) {
      this.apiQuery.addIncludes(this.includes);
    }

    this.input$
      .pipe(
        tap(() => (this.loader = true)),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((term: any) => {
          // ако е подаден geofence атрибут на компонента включва търсенето на хотел в определен радиус
          if (this.geofenceFilter) {
            const radius = 100; // km

            // ако има валидни координати добавя geofence филтъра, ако не го премахва
            if (this.geofenceFilter.lat && this.geofenceFilter.long) {
              const geoFenceFilter = new GeoFenceFilter();
              geoFenceFilter.lat = Number(this.geofenceFilter.lat);
              geoFenceFilter.long = Number(this.geofenceFilter.long);
              geoFenceFilter.inner_radius = 0;
              geoFenceFilter.outer_radius = radius;
              this.apiQuery.setGeoFence(geoFenceFilter);
            } else {
              this.apiQuery.removeGeoFence();
            }
          }

          // допълнителен филтър
          if (this.filter && this.filterValue) {
            this.apiQuery.addFilter(this.filter, this.filterValue);
          }

          // За да ме прави излишен request след като се избере нещо
          if (!term) {
            return of(this.lastResponse);
          }

          return this.hotelService
            .index(this.apiQuery.addIncludes('city.country', 'venues').addFilters({ name: term }).setLimit(30))
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.loader = false))
            );
        })
      )
      .subscribe((hotels: PaginatedResponse) => {
        this.data = of(hotels.data);
        this.lastResponse = hotels;
        this.loader = false;
      });
  }

  add(searchTerm: string) {
    this.hotelFormService.open(new Hotel().deserialize({ name: searchTerm })).then(
      (response) => {
        this.loadHotelById(response.id);
        this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
      },
      (err) => {
        log.error(err);
      }
    );
  }

  change(hotel: any) {
    this.select.emit(hotel);
    if (this.parentFormGroup.get(this.hotelFormControlName)) {
      this.parentFormGroup.get(this.hotelFormControlName).patchValue(hotel);
    }
  }

  changeModel(event: any) {
    this.modelChange.emit(event);
  }

  edit() {
    if (this.parentFormGroup.get(this.passFormControlName).value) {
      this.hotelFormService
        .open(new Hotel().deserialize({ id: this.parentFormGroup.get(this.passFormControlName).value }))
        .then(
          (response) => {
            this.loadHotelById(response.id);
            this.parentFormGroup.get(this.passFormControlName).setValue(response.id);
            if (this.parentFormGroup.get(this.hotelFormControlName)) {
              this.parentFormGroup.get(this.hotelFormControlName).patchValue(response);
            }
          },
          (err) => {
            log.error(err);
          }
        );
    }
  }
}
